import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

type Section = 'chat' | 'documents' | 'examination' | 'resources' | 'document' | 'drawings' |
                'template' | 'admin' | 'ids' | 'uspto' | 'tasks' | 'builder' | 'relate' |
                'document_table' | 'applications' | 'saved_sessions' | 'settings' | 'usage' | 'account';

interface UIState {
  // Current view
  selectedSection: Section;

  // Loading states
  chatResponseLoading: boolean;
  isLoadingSession: boolean;
  isLoadingDocs: boolean;
  foldersLoading: boolean;

  // Modal states
  showWelcome: boolean;
  showGuide: boolean;
  showSettings: boolean;
  showAccountWindow: boolean;
  showSessionListWindow: boolean;
  showFeedbackWindow: boolean;
  showDisclaimer: boolean;
  showSaveAsNewSessionModal: boolean;
  showCreditsUnavailableModal: boolean;

  // Task/Operation states
  showTasks: boolean;
  showTaskStatusPanel: boolean;
  showCustomAnalysis: boolean;
  showUniversalTaskPanel: boolean;
  displayOperationResponse: boolean;
  displayIssues: boolean;
  displayProsHist: boolean;
  displayClaimSupportMap: boolean;
  displaySpecMap: boolean;
  displayPartsList: boolean;
  showCustomSummaryResponses: boolean;

  // UI element visibility
  showWelcomeSuggestions: boolean;
  isContextVisible: boolean;
  isSuppContextVisible: boolean;

  // Toast/notification states
  toasts: Record<string, unknown>;
  loadingToasts: Array<{
    id: string | number;
    message: string;
    highlight?: boolean;
    secondary?: boolean;
    auto_hide?: boolean;
    num_seconds_visible?: number;
  }>;

  // Actions
  setSelectedSection: (section: Section) => void;

  setChatResponseLoading: (loading: boolean) => void;
  setIsLoadingSession: (loading: boolean) => void;
  setIsLoadingDocs: (loading: boolean) => void;
  setFoldersLoading: (loading: boolean) => void;

  setShowWelcome: (show: boolean) => void;
  setShowGuide: (show: boolean) => void;
  setShowSettings: (show: boolean) => void;
  setShowAccountWindow: (show: boolean) => void;
  setShowSessionListWindow: (show: boolean) => void;
  setShowFeedbackWindow: (show: boolean) => void;
  setShowDisclaimer: (show: boolean) => void;
  setShowSaveAsNewSessionModal: (show: boolean) => void;
  setShowCreditsUnavailableModal: (show: boolean) => void;

  setShowTasks: (show: boolean) => void;
  setShowTaskStatusPanel: (show: boolean) => void;
  setShowCustomAnalysis: (show: boolean) => void;
  setShowUniversalTaskPanel: (show: boolean) => void;
  setDisplayOperationResponse: (show: boolean) => void;
  setDisplayIssues: (show: boolean) => void;
  setDisplayProsHist: (show: boolean) => void;
  setDisplayClaimSupportMap: (show: boolean) => void;
  setDisplaySpecMap: (show: boolean) => void;
  setDisplayPartsList: (show: boolean) => void;
  setShowCustomSummaryResponses: (show: boolean) => void;

  setShowWelcomeSuggestions: (show: boolean) => void;
  setIsContextVisible: (visible: boolean) => void;
  setIsSuppContextVisible: (visible: boolean) => void;

  addToast: (id: string, toast: unknown) => void;
  removeToast: (id: string) => void;
  addLoadingToast: (toast: {
    id: string | number;
    message: string;
    highlight?: boolean;
    secondary?: boolean;
    auto_hide?: boolean;
    num_seconds_visible?: number;
  }) => void;
  removeLoadingToast: (id: string | number) => void;
  updateLoadingToast: (id: string | number, updates: Partial<{
    message: string;
    highlight?: boolean;
    secondary?: boolean;
    auto_hide?: boolean;
    num_seconds_visible?: number;
  }>) => void;
}

export const useUIStore = create<UIState>()(
  devtools((set) => ({
    // Initial state
    selectedSection: 'chat',

    chatResponseLoading: false,
    isLoadingSession: false,
    isLoadingDocs: false,
    foldersLoading: false,

    showWelcome: false,
    showGuide: false,
    showSettings: false,
    showAccountWindow: false,
    showSessionListWindow: false,
    showFeedbackWindow: false,
    showDisclaimer: false,
    showSaveAsNewSessionModal: false,
    showCreditsUnavailableModal: false,

    showTasks: false,
    showTaskStatusPanel: false,
    showCustomAnalysis: false,
    showUniversalTaskPanel: false,
    displayOperationResponse: false,
    displayIssues: false,
    displayProsHist: false,
    displayClaimSupportMap: false,
    displaySpecMap: false,
    displayPartsList: false,
    showCustomSummaryResponses: false,

    showWelcomeSuggestions: true,
    isContextVisible: true,
    isSuppContextVisible: false,

    toasts: {},
    loadingToasts: [],

    // Actions
    setSelectedSection: (section) => set({ selectedSection: section }),

    setChatResponseLoading: (loading) => set({ chatResponseLoading: loading }),
    setIsLoadingSession: (loading) => set({ isLoadingSession: loading }),
    setIsLoadingDocs: (loading) => set({ isLoadingDocs: loading }),
    setFoldersLoading: (loading) => set({ foldersLoading: loading }),

    setShowWelcome: (show) => set({ showWelcome: show }),
    setShowGuide: (show) => set({ showGuide: show }),
    setShowSettings: (show) => set({ showSettings: show }),
    setShowAccountWindow: (show) => set({ showAccountWindow: show }),
    setShowSessionListWindow: (show) => set({ showSessionListWindow: show }),
    setShowFeedbackWindow: (show) => set({ showFeedbackWindow: show }),
    setShowDisclaimer: (show) => set({ showDisclaimer: show }),
    setShowSaveAsNewSessionModal: (show) => set({ showSaveAsNewSessionModal: show }),
    setShowCreditsUnavailableModal: (show) => set({ showCreditsUnavailableModal: show }),

    setShowTasks: (show) => set({ showTasks: show }),
    setShowTaskStatusPanel: (show) => set({ showTaskStatusPanel: show }),
    setShowCustomAnalysis: (show) => set({ showCustomAnalysis: show }),
    setShowUniversalTaskPanel: (show) => set({ showUniversalTaskPanel: show }),
    setDisplayOperationResponse: (show) => set({ displayOperationResponse: show }),
    setDisplayIssues: (show) => set({ displayIssues: show }),
    setDisplayProsHist: (show) => set({ displayProsHist: show }),
    setDisplayClaimSupportMap: (show) => set({ displayClaimSupportMap: show }),
    setDisplaySpecMap: (show) => set({ displaySpecMap: show }),
    setDisplayPartsList: (show) => set({ displayPartsList: show }),
    setShowCustomSummaryResponses: (show) => set({ showCustomSummaryResponses: show }),

    setShowWelcomeSuggestions: (show) => set({ showWelcomeSuggestions: show }),
    setIsContextVisible: (visible) => set({ isContextVisible: visible }),
    setIsSuppContextVisible: (visible) => set({ isSuppContextVisible: visible }),

    addToast: (id, toast) => set((state) => ({
      toasts: { ...state.toasts, [id]: toast }
    })),

    removeToast: (id) => set((state) => {
      const { [id]: _, ...rest } = state.toasts;
      return { toasts: rest };
    }),

    addLoadingToast: (toast) => set((state) => ({
      loadingToasts: [...state.loadingToasts, toast]
    })),

    removeLoadingToast: (id) => set((state) => ({
      loadingToasts: state.loadingToasts.filter(t => t.id !== id)
    })),

    updateLoadingToast: (id, updates) => set((state) => ({
      loadingToasts: state.loadingToasts.map(t =>
        t.id === id ? { ...t, ...updates } : t
      )
    })),
  }))
);
